// The shape of a product's solid body: which components are plain boxes, and
// which of their faces can be seen from outside the product.
import * as THREE from 'three';
import type {PlanAxis} from 'components/customer/RoomPlanner/helpers/calculations';
import {axisIndex} from 'components/customer/RoomPlanner/helpers/axes';
import {flattenBox} from 'components/customer/RoomPlanner/helpers/boxes';

/**
 * One upright face of a solid component that can be seen from outside the
 * product.
 */
export interface ExteriorFace {
    axis: PlanAxis;
    // Which way the face looks along its axis.
    side: -1 | 1;
    position: number;
    // The face's own extent: the component's box, flattened onto the face.
    rect: THREE.Box3;
}

type PositionAttribute =
    THREE.BufferAttribute | THREE.InterleavedBufferAttribute;

// Whether each geometry is a plain box, by its vertices and the version they
// were read at. Answering reads every vertex, and every drag asks it of every
// blocking product's components, so the answer is kept until the vertices
// change: three.js bumps an attribute's version whenever they do.
const boxShapes = new WeakMap<
    PositionAttribute,
    {version: number; boxShaped: boolean}
>();

/**
 * @param {PositionAttribute} position A geometry's vertices.
 * @return {number} Their version, which changes whenever they do.
 */
const versionOf = (position: PositionAttribute): number =>
    'isInterleavedBufferAttribute' in position
        ? position.data.version
        : position.version;

/**
 * @param {THREE.Mesh} mesh A component.
 * @return {boolean} True when its geometry is a plain box, every vertex at a
 * corner of its bounding box. An L-shaped shelf or top is not: its box fills
 * the notch. Kept with the geometry until its vertices change.
 */
export const isBoxShaped = (mesh: THREE.Mesh): boolean => {
    const position = mesh.geometry?.getAttribute('position');
    if (!position) return false;

    const known = boxShapes.get(position);
    if (known?.version === versionOf(position)) return known.boxShaped;

    const boxShaped = readIsBoxShaped(mesh, position);
    boxShapes.set(position, {version: versionOf(position), boxShaped});

    return boxShaped;
};

/**
 * @param {THREE.Mesh} mesh A component.
 * @param {PositionAttribute} position Its geometry's vertices.
 * @return {boolean} Whether every vertex is at a corner of its bounding box.
 */
const readIsBoxShaped = (
    mesh: THREE.Mesh,
    position: PositionAttribute
): boolean => {
    if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
    const bounds = mesh.geometry.boundingBox;
    if (!bounds || bounds.isEmpty()) return false;

    // Near enough to an edge, per axis, that a bevelled door still counts as
    // a box, while the notch of an L-shaped component, mid-span, does not.
    const size = bounds.getSize(new THREE.Vector3());
    const vertex = new THREE.Vector3();
    const atCorner = (axis: 0 | 1 | 2) => {
        const tolerance = Math.max(size.getComponent(axis) * 0.1, 2);

        return (
            Math.abs(
                vertex.getComponent(axis) - bounds.min.getComponent(axis)
            ) <= tolerance ||
            Math.abs(
                vertex.getComponent(axis) - bounds.max.getComponent(axis)
            ) <= tolerance
        );
    };

    for (let i = 0; i < position.count; i++) {
        vertex.fromBufferAttribute(position, i);
        if (!atCorner(0) || !atCorner(1) || !atCorner(2)) return false;
    }

    return true;
};

// The boxes each shaped geometry is cut into, in its own space, by its
// vertices and the version they were read at, kept as isBoxShaped's answers
// are; null for a shape left as its box.
const shapeCuts = new WeakMap<
    PositionAttribute,
    {version: number; boxes: THREE.Box3[] | null}
>();

// Vertex coordinates this close (0.005 mm) are one.
const SAME_CUT = 0.001;

// How near a triangle must face to straight along an axis to count as a
// straight side square to it: tighter than the first and last segments of
// a curve, which lean a degree or two.
const SQUARE = 1e-6;

// An outline with more distinct coordinates than this either way would cut
// into a box per sliver: it is left as its box.
const MAX_CUTS = 48;

/**
 * @param {THREE.Mesh} mesh A component that is not a plain box.
 * @return {THREE.Box3[] | null} The world boxes it is made of, when it is an
 * outline extruded straight through its thickness, as an L-shape, Key-shape
 * or U-shape panel is: its outline cut into rows of boxes, so its faces stop
 * at a cut-out rather than running across it as its box's do. Null for any
 * other shape. Kept with the geometry until its vertices change.
 */
export const extrudedBoxes = (mesh: THREE.Mesh): THREE.Box3[] | null => {
    const position = mesh.geometry?.getAttribute('position');
    if (!position) return null;

    let known = shapeCuts.get(position);
    if (known?.version !== versionOf(position)) {
        known = {
            version: versionOf(position),
            boxes: readExtrudedBoxes(mesh.geometry, position),
        };
        shapeCuts.set(position, known);
    }
    if (!known.boxes) return null;

    mesh.updateWorldMatrix(true, false);

    return known.boxes.map((box) => box.clone().applyMatrix4(mesh.matrixWorld));
};

/**
 * @param {number[]} values Coordinates along one axis.
 * @return {number[]} Each once, in order.
 */
const distinctCuts = (values: number[]): number[] =>
    values
        .sort((a, b) => a - b)
        .filter(
            (value, i, all) =>
                i === 0 || value - (all.at(i - 1) ?? 0) > SAME_CUT
        );

/**
 * @param {number[]} point A point, across and up the outline.
 * @param {Array<number[]>} triangle Three corners, likewise.
 * @return {boolean} True when the point is inside the triangle or on its
 * edge.
 */
const inTriangle = (point: number[], triangle: number[][]): boolean => {
    const [x, y] = point;
    const [[ax, ay], [bx, by], [cx, cy]] = triangle;
    const side = (px: number, py: number, qx: number, qy: number) =>
        (qx - px) * (y - py) - (qy - py) * (x - px);
    const sides = [
        side(ax, ay, bx, by),
        side(bx, by, cx, cy),
        side(cx, cy, ax, ay),
    ];

    return (
        sides.every((s) => s >= -SAME_CUT) || sides.every((s) => s <= SAME_CUT)
    );
};

/**
 * @param {THREE.Box3[]} boxes Boxes in a grid.
 * @param {number} axis The axis index to join them along.
 * @return {THREE.Box3[]} Boxes that touch along the axis and match the other
 * ways, joined.
 */
const mergeAlong = (
    boxes: readonly THREE.Box3[],
    axis: number
): THREE.Box3[] => {
    const others = [0, 1, 2].filter((other) => other !== axis);
    const near = (a: number, b: number) => Math.abs(a - b) <= SAME_CUT;

    return boxes.reduce<THREE.Box3[]>((merged, next) => {
        const before = merged.find(
            (box) =>
                near(box.max.getComponent(axis), next.min.getComponent(axis)) &&
                others.every(
                    (other) =>
                        near(
                            box.min.getComponent(other),
                            next.min.getComponent(other)
                        ) &&
                        near(
                            box.max.getComponent(other),
                            next.max.getComponent(other)
                        )
                )
        );
        if (before) {
            before.max.setComponent(axis, next.max.getComponent(axis));
        } else {
            merged.push(next.clone());
        }

        return merged;
    }, []);
};

/**
 * @param {THREE.BufferGeometry} geometry A component's geometry.
 * @param {PositionAttribute} position Its vertices.
 * @return {THREE.Box3[] | null} Its end cap cut into boxes at its straight
 * sides, in its own space; null when it is not an extrusion or is just its box.
 */
const readExtrudedBoxes = (
    geometry: THREE.BufferGeometry,
    position: PositionAttribute
): THREE.Box3[] | null => {
    const index = geometry.getIndex();
    const points = Array.from({length: position.count}, (_, i) =>
        new THREE.Vector3().fromBufferAttribute(position, i)
    );
    const valuesAlong = (axis: number, from: readonly THREE.Vector3[]) =>
        from.map((point) => point.getComponent(axis));

    // An extrusion has exactly two coordinates through its thickness.
    const through = [0, 1, 2].find(
        (axis) => distinctCuts(valuesAlong(axis, points)).length === 2
    );
    if (through === undefined) return null;
    const [u, v] = [0, 1, 2].filter((axis) => axis !== through);
    const [back, front] = distinctCuts(valuesAlong(through, points));

    const triangles = Array.from(
        {length: Math.floor((index ? index.count : position.count) / 3)},
        (_, t) =>
            [0, 1, 2].map(
                (k) =>
                    points.at(index ? index.getX(t * 3 + k) : t * 3 + k) ??
                    new THREE.Vector3()
            )
    );

    const facing = ([a, b, c]: THREE.Vector3[]): number[] => {
        const normal = new THREE.Vector3()
            .subVectors(b, a)
            .cross(new THREE.Vector3().subVectors(c, a))
            .normalize();

        return [0, 1, 2].filter(
            (axis) => Math.abs(normal.getComponent(axis)) > 1 - SQUARE
        );
    };

    const isBackCap = (triangle: THREE.Vector3[]): boolean =>
        facing(triangle).includes(through) &&
        triangle.every(
            (point) => Math.abs(point.getComponent(through) - back) <= SAME_CUT
        );

    const isSquareSide = (triangle: THREE.Vector3[]): boolean => {
        const axes = facing(triangle);

        return axes.length === 1 && !axes.includes(through);
    };

    const bounds = new THREE.Box3().setFromPoints(points);
    const cutsAlong = (axis: number, sides: readonly THREE.Vector3[]) =>
        distinctCuts([
            bounds.min.getComponent(axis),
            bounds.max.getComponent(axis),
            ...valuesAlong(axis, sides),
        ]);

    const cap = triangles
        .filter(isBackCap)
        .map((triangle) =>
            triangle.map((point) => [
                point.getComponent(u),
                point.getComponent(v),
            ])
        );
    const sides = triangles.filter(isSquareSide).flat();
    const acrossCuts = cutsAlong(u, sides);
    const upCuts = cutsAlong(v, sides);
    if (!cap.length || Math.max(acrossCuts.length, upCuts.length) > MAX_CUTS) {
        return null;
    }

    const cut = (cuts: number[], i: number) => cuts.at(i) ?? 0;
    const middle = (cuts: number[], i: number) =>
        (cut(cuts, i) + cut(cuts, i + 1)) / 2;

    const filled = (i: number, j: number): boolean =>
        cap.some((triangle) =>
            inTriangle([middle(acrossCuts, i), middle(upCuts, j)], triangle)
        );

    const cellBox = (i: number, j: number): THREE.Box3 =>
        new THREE.Box3(
            new THREE.Vector3()
                .setComponent(u, cut(acrossCuts, i))
                .setComponent(v, cut(upCuts, j))
                .setComponent(through, back),
            new THREE.Vector3()
                .setComponent(u, cut(acrossCuts, i + 1))
                .setComponent(v, cut(upCuts, j + 1))
                .setComponent(through, front)
        );

    // Each cell the end cap covers, joined into runs along each row, then
    // with the matching run in the row beside it.
    const cells = upCuts
        .slice(1)
        .flatMap((_, j) =>
            acrossCuts
                .slice(1)
                .flatMap((__, i) => (filled(i, j) ? [cellBox(i, j)] : []))
        );
    const boxes = mergeAlong(mergeAlong(cells, u), v);

    return boxes.length > 1 ? boxes : null;
};

// A component that doesn't fill its box, as a corner 45's shelf or a door
// turned 45 degrees, collides as strips this wide (5 mm).
const STRIP = 1;

/**
 * @param {THREE.Mesh} mesh A component.
 * @return {boolean} True when each of its sides runs along the room's axes.
 */
const isSquareToRoom = (mesh: THREE.Mesh): boolean =>
    [0, 1, 2].every((axis) => {
        const {x, z} = new THREE.Vector3()
            .setFromMatrixColumn(mesh.matrixWorld, axis)
            .normalize();

        return Math.min(Math.abs(x), Math.abs(z)) <= SAME_CUT;
    });

/**
 * @param {THREE.Mesh} mesh A component.
 * @return {Array<THREE.Vector3[]>} Its triangles, in world space.
 */
const worldTriangles = (mesh: THREE.Mesh): THREE.Vector3[][] => {
    const position = mesh.geometry.getAttribute('position');
    const index = mesh.geometry.getIndex();
    const corner = (i: number) =>
        new THREE.Vector3()
            .fromBufferAttribute(position, index ? index.getX(i) : i)
            .applyMatrix4(mesh.matrixWorld);

    return Array.from(
        {length: Math.floor((index ? index.count : position.count) / 3)},
        (_, t) => [corner(t * 3), corner(t * 3 + 1), corner(t * 3 + 2)]
    );
};

/**
 * @param {number} axis An axis index.
 * @param {number} bound Where to cut along it.
 * @param {boolean} below Keep the part below the bound, else above.
 * @return {Function} Cuts a flat polygon there, keeping that part.
 */
const clip =
    (axis: number, bound: number, below: boolean) =>
    (polygon: THREE.Vector3[]): THREE.Vector3[] => {
        const kept = (point: THREE.Vector3) =>
            below
                ? point.getComponent(axis) <= bound
                : point.getComponent(axis) >= bound;
        const crossing = (point: THREE.Vector3, next: THREE.Vector3) =>
            point
                .clone()
                .lerp(
                    next,
                    (bound - point.getComponent(axis)) /
                        (next.getComponent(axis) - point.getComponent(axis))
                );

        return polygon.flatMap((point, i) => {
            const next = polygon.at((i + 1) % polygon.length) ?? point;

            return [
                ...(kept(point) ? [point] : []),
                ...(kept(point) === kept(next) ? [] : [crossing(point, next)]),
            ];
        });
    };

/**
 * @param {THREE.Box3} part A box the component is in.
 * @param {Array<THREE.Vector3[]>} triangles The component's world triangles.
 * @return {THREE.Box3[]} The box in strips along its longer side, each
 * shrunk to the component within it, alike strips joined.
 */
const strips = (
    part: THREE.Box3,
    triangles: THREE.Vector3[][]
): THREE.Box3[] => {
    const size = part.getSize(new THREE.Vector3());
    const along = size.x >= size.z ? 0 : 2;
    const start = part.min.getComponent(along);
    const count = Math.max(1, Math.ceil(size.getComponent(along) / STRIP));
    const width = size.getComponent(along) / count;

    // Each triangle in the strips it spans, so a strip clips only those.
    const bins = Array.from({length: count}, (): THREE.Vector3[][] => []);
    const binOf = (value: number) =>
        Math.min(count - 1, Math.max(0, Math.floor((value - start) / width)));
    triangles.forEach((triangle) => {
        const values = triangle.map((point) => point.getComponent(along));
        const [low, high] = [Math.min(...values), Math.max(...values)];
        if (high < start || low > part.max.getComponent(along)) return;
        for (let i = binOf(low); i <= binOf(high); i++) {
            bins.at(i)?.push(triangle);
        }
    });
    const withinPart = [0, 1, 2]
        .filter((axis) => axis !== along)
        .flatMap((axis) => [
            clip(axis, part.min.getComponent(axis), false),
            clip(axis, part.max.getComponent(axis), true),
        ]);

    const strip = (i: number): THREE.Box3 => {
        const low = start + width * i;
        const cuts = [
            clip(along, low, false),
            clip(along, low + width, true),
            ...withinPart,
        ];
        const within = (triangle: THREE.Vector3[]) =>
            cuts.reduce((polygon, cut) => cut(polygon), triangle);

        return new THREE.Box3().setFromPoints(
            (bins.at(i) ?? []).flatMap(within)
        );
    };

    return mergeAlong(
        Array.from({length: count}, (_, i) => strip(i)).filter(
            (box) => !box.isEmpty()
        ),
        along
    );
};

/**
 * @param {THREE.Mesh} mesh A component, its world matrix current.
 * @return {THREE.Box3[]} What a drag collides with (CO-5961): its box, or
 * strips of it where it doesn't fill it.
 */
export const collisionBoxes = (mesh: THREE.Mesh): THREE.Box3[] => {
    const box = new THREE.Box3().setFromObject(mesh);
    if (box.isEmpty()) return [];
    const cut = isBoxShaped(mesh) ? [box] : extrudedBoxes(mesh);
    if (cut && isSquareToRoom(mesh)) return cut;
    const triangles = worldTriangles(mesh);

    return (cut ?? [box]).flatMap((part) => strips(part, triangles));
};

/**
 * @param {THREE.Box3} bbox A box.
 * @return {ExteriorFace[]} Its four upright faces.
 */
export const bboxFaces = (bbox: THREE.Box3): ExteriorFace[] =>
    (['x', 'z'] as PlanAxis[]).flatMap((axis) =>
        ([-1, 1] as const).map((side) => {
            const index = axisIndex(axis);
            const position =
                side < 0
                    ? bbox.min.getComponent(index)
                    : bbox.max.getComponent(index);

            return {
                axis,
                side,
                position,
                rect: flattenBox(bbox, index, position),
            };
        })
    );

// Where on a face its visibility is sampled, as fractions across each way.
const FACE_SAMPLES = [0.1, 0.5, 0.9];

// How far a neighbouring component must reach past a face to hide it, so one
// sitting flush beside it does not.
const COVER_EPSILON = 0.01;

// Gaps between components up to this wide (5 mm) count as closed when judging
// whether a face can be seen, so a shelf doesn't show between a pair of doors
// and a carcase front doesn't show past a door's overhang.
const COVER_GAP = 1;

/**
 * A product's exterior faces: each upright face of each solid component that
 * can be seen from outside the product somewhere across it. A face is seen at
 * a point when looking straight out from it meets none of the other
 * components, with gaps up to COVER_GAP between them closed. Sampled at nine
 * points, so a face hidden except for a sliver at its edge may be missed.
 * @param {THREE.Box3[]} bboxes The boxes of the product's solid components.
 * @return {ExteriorFace[]} The faces.
 */
export const exteriorFaces = (bboxes: readonly THREE.Box3[]): ExteriorFace[] =>
    bboxes.flatMap((bbox, index) =>
        bboxFaces(bbox).filter(({axis, side, position}) => {
            const across: PlanAxis = axis === 'x' ? 'z' : 'x';
            const a = axisIndex(axis);
            const c = axisIndex(across);

            return FACE_SAMPLES.some((u) =>
                FACE_SAMPLES.some((v) => {
                    const along =
                        bbox.min.getComponent(c) +
                        u *
                            (bbox.max.getComponent(c) -
                                bbox.min.getComponent(c));
                    const height = bbox.min.y + v * (bbox.max.y - bbox.min.y);

                    return !bboxes.some(
                        (other, otherIndex) =>
                            otherIndex !== index &&
                            other.min.getComponent(c) - COVER_GAP < along &&
                            along < other.max.getComponent(c) + COVER_GAP &&
                            other.min.y - COVER_GAP < height &&
                            height < other.max.y + COVER_GAP &&
                            (side > 0
                                ? other.max.getComponent(a) >
                                  position + COVER_EPSILON
                                : other.min.getComponent(a) <
                                  position - COVER_EPSILON)
                    );
                })
            );
        })
    );
